import React, { FC } from 'react';
import { DateRange as ReactDateRange } from 'react-date-range';
import { Box, IconButton, Popover, Stack, styled, Typography } from '@mui/material';
import { bindPopover, type PopupState } from 'material-ui-popup-state/hooks';

import ChevronLeft from '@/assets/icons/chevron-left.svg?react';
import ChevronRight from '@/assets/icons/chevron-right.svg?react';
import { useDirection } from '@/hooks/useDirection';
import typography from '@/theme/typography';

import { safeFormat } from './utils';

const DATE_MONTH_FORMAT = 'MMM yyyy';

const DAY_CELL_SIZE = 40;
const DAYS_A_WEEK = 7;

interface CalendarPopoverProps {
  popupState: PopupState;
  value: {
    startDate: Date;
    endDate: Date;
    key: string;
    color: string;
  }[];
  onChange: (startDate: Date, endDate: Date, close: VoidFunction) => void;
}

const CalendarPopover: FC<CalendarPopoverProps> = ({ popupState, value, onChange }) => {
  const { start } = useDirection();

  return (
    <Popover
      {...bindPopover(popupState)}
      PaperProps={{
        elevation: 1,
        sx: {
          padding: 3,
          bgcolor: 'background.default'
        }
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left'
      }}
      anchorOrigin={{ horizontal: start, vertical: 'bottom' }}
    >
      <StyledCalendarBox>
        <ReactDateRange
          navigatorRenderer={(focusedDate, changeShownDate) => (
            <Stack direction="row" mb={3} alignItems="center" justifyContent="space-between">
              <Typography sx={typography.body2}>{safeFormat(focusedDate, DATE_MONTH_FORMAT)}</Typography>
              <Stack direction="row" gap={1} sx={{ '& .MuiIconButton-root': { p: 0 } }}>
                <IconButton onClick={() => changeShownDate(-1, 'monthOffset')}>
                  <ChevronLeft />
                </IconButton>
                <IconButton onClick={() => changeShownDate(1, 'monthOffset')}>
                  <ChevronRight />
                </IconButton>
              </Stack>
            </Stack>
          )}
          displayMode="dateRange"
          showDateDisplay={false}
          ranges={value}
          onChange={({ range }) => onChange(range.startDate!, range.endDate!, popupState.close)}
          weekdayDisplayFormat="ccccc"
        />
      </StyledCalendarBox>
    </Popover>
  );
};

const StyledCalendarBox = styled(Box)(({ theme }) => ({
  '& .rdrDayNumber': {
    fontWeight: typography.fontWeightRegular
  },
  '& .rdrDayNumber span': {
    zIndex: 1
  },
  '& .rdrMonth': {
    padding: 0,
    width: DAY_CELL_SIZE * DAYS_A_WEEK
  },
  '& .rdrDay, & .rdrWeekDay': {
    width: DAY_CELL_SIZE,
    height: DAY_CELL_SIZE
  },
  '& .rdrDayPassive, & .rdrWeekDay': {
    color: 'grey.700'
  },
  '& .rdrEndEdge, & .rdrStartEdge, & .rdrInRange, & .rdrDayInPreview, & .rdrDayStartPreview, & .rdrDayEndPreview': {
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    borderTopLeftRadius: 'unset',
    borderTopRightRadius: 'unset',
    borderBottomLeftRadius: 'unset',
    borderBottomRightRadius: 'unset'
  },
  '& .rdrStartEdge, & .rdrDayStartPreview, & .rdrDayStartOfWeek .rdrInRange, & .rdrDayStartOfWeek .rdrDayInPreview': {
    borderStartStartRadius: theme.spacing(0.5),
    borderEndStartRadius: theme.spacing(0.5)
  },
  '& .rdrEndEdge, & .rdrDayEndPreview, & .rdrDayEndOfWeek .rdrInRange, & .rdrDayEndOfWeek .rdrDayInPreview': {
    borderStartEndRadius: theme.spacing(0.5),
    borderEndEndRadius: theme.spacing(0.5)
  },
  '& .rdrStartEdge, & .rdrDayStartPreview, & .rdrEndEdge, & .rdrDayEndPreview': {
    backgroundColor: theme.palette.primary[250],
    color: theme.palette.text.primary
  },
  '& .rdrDayInPreview, & .rdrInRange': {
    backgroundColor: theme.palette.primary[100],
    border: 'none'
  },
  '& .rdrDayToday .rdrDayNumber span::after': {
    backgroundColor: theme.palette.primary.main
  },
  '& .rdrDay:not(.rdrDayPassive) .rdrDayNumber span': {
    color: theme.palette.text.primary,
    ...typography.body3
  }
}));

export default CalendarPopover;
